// Organizations someone can join with their company email address (the organization verified the domain).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type Me } from "../../lib/api";
import { Alert, Button, cx } from "../../components/ui";

type Joinable = { id: string; name: string; slug: string; join: "request" | "auto"; requestId: string | null };

export function JoinableOrgs({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // While a request waits, look every 15 s; once it's approved the organization leaves this list: go straight in.
  const data = useQuery({
    queryKey: ["joinable"], queryFn: () => api<{ organizations: Joinable[] }>("/me/joinable"), staleTime: 60_000,
    refetchInterval: (q) => (q.state.data?.organizations.some((o) => o.requestId) ? 15_000 : false),
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = data.data?.organizations ?? [];
  const asked = useRef(new Map<string, string>());
  useEffect(() => {
    const now = new Map(list.filter((o) => o.requestId).map((o) => [o.id, o.slug]));
    for (const [id, slug] of asked.current) {
      if (list.some((o) => o.id === id)) continue;
      void qc.invalidateQueries({ queryKey: ["me"] }).then(() => {
        if (qc.getQueryData<Me>(["me"])?.organizations.some((o) => o.slug === slug)) navigate(`/o/${slug}`);
      });
      break;
    }
    asked.current = now;
  }, [list, qc, navigate]);
  if (!list.length) return null;
  async function join(o: Joinable) {
    setBusy(o.id); setError(null);
    try {
      const r = await api<{ status: "joined" | "requested"; slug: string }>(`/me/joinable/${o.id}`, { method: "POST" });
      await qc.invalidateQueries({ queryKey: ["joinable"] });
      if (r.status === "joined") { await qc.invalidateQueries({ queryKey: ["me"] }); navigate(`/o/${r.slug}`); }
    } catch (err) { setError(err instanceof ApiError ? err.message : "That didn't work. Try again."); } finally { setBusy(null); }
  }
  async function withdraw(o: Joinable) {
    setBusy(o.id);
    try { await api(`/me/join-requests/${o.requestId}`, { method: "DELETE" }); await qc.invalidateQueries({ queryKey: ["joinable"] }); } finally { setBusy(null); }
  }
  return (
    <div className={cx(compact ? "border-t border-slate-100 px-1 pt-1 dark:border-slate-800" : "mb-6 rounded-xl bg-brand-50 p-4 dark:bg-brand-700/20")}>
      <p className={cx(compact ? "px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500" : "mb-2 text-sm font-medium")}>{compact ? "You can join" : "Your company is already here"}</p>
      {error && <div className="mb-2"><Alert>{error}</Alert></div>}
      <ul className="space-y-1">
        {list.map((o) => (
          <li key={o.id} className="flex items-center justify-between gap-2 px-2 py-1 text-sm">
            <span className="min-w-0 truncate font-medium">{o.name}</span>
            {o.requestId
              ? <span className="flex items-center gap-2 text-xs text-slate-500">Asked<button type="button" onClick={() => withdraw(o)} disabled={busy === o.id} className="text-brand-600 hover:underline">Withdraw</button></span>
              : <Button size="sm" variant={compact ? "ghost" : "primary"} onClick={() => join(o)} loading={busy === o.id}>{o.join === "auto" ? "Join" : "Ask to join"}</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}
